/* @flow */
import ApolloClient from 'apollo-client';
import idx from 'idx';
import update from 'immutability-helper';
import { getApolloClientSingleton } from 'lib/apollo/client/browser';

import { currentReactionSummaryVersion, saveReactionSummary } from 'chat/components/Reaction';
import { FIRST_PAGE_VAR as ANNOUNCEMENT_FIRST_PAGE_VAR } from 'chat/components/AnnouncementStream/components/AnnouncementStreamContainer';
import { FIRST_PAGE_VAR as CHAT_FIRST_PAGE_VAR } from 'chat/components/ChatStream/components/ChatStreamContainer';
import {
  addStreamSequenceItem,
  updateChatStreamPreview,
} from 'chat/components/ChatStream/utils/actions';
import { updateEntityStreamPreview } from 'chat/components/AnnouncementStream/utils/actions';
import ChatMessage from 'entities/ChatMessage';
import GET_ANNOUNCEMENT_STREAM_ITEMS_QUERY from 'chat/components/AnnouncementStream/queries/GetAnnouncementStreamItems.gql';
import GET_CHAT_STREAM_ITEMS_QUERY from 'chat/components/ChatStream/queries/GetChatStreamItems.gql';
import STREAM_SECTION_CHATS_QUERY from 'chat/components/StreamSectionList/queries/StreamSectionChatsQuery.gql';

type Options = {
  client: ApolloClient,
};

class Handler {
  client: ApolloClient;

  constructor(options: Options) {
    this.client = options.client;
  }

  insertIntoEntityStream(streamUUID: string, item: *) {
    updateEntityStreamPreview(streamUUID, new Date(), item.preview);

    try {
      const streamIsLoaded = getApolloClientSingleton().readQuery({
        query: GET_ANNOUNCEMENT_STREAM_ITEMS_QUERY,
        variables: { uuid: streamUUID, ...ANNOUNCEMENT_FIRST_PAGE_VAR },
      });
      if (streamIsLoaded) {
        this.client.query({
          fetchPolicy: 'network-only',
          query: GET_ANNOUNCEMENT_STREAM_ITEMS_QUERY,
          variables: {
            uuid: streamUUID,
            ...ANNOUNCEMENT_FIRST_PAGE_VAR,
          },
        });
      }
    } catch (e) {
      console.log('ApolloPushHandler - stream does not exist or is not yet loaded in the store');
    }
  }

  insertIntoChatStream(chatUUID: string, item: *) {
    const message = ChatMessage.fromJSON(item);
    addStreamSequenceItem(chatUUID, message.toGraphQL());
    updateChatStreamPreview(chatUUID, new Date(), item.preview);

    this.client
      .query({
        query: GET_CHAT_STREAM_ITEMS_QUERY,
        variables: {
          uuid: chatUUID,
          ...CHAT_FIRST_PAGE_VAR,
        },
      })
      .then(
        res => {
          const newStreams = res.data.chatStreams;
          const streamRelatedGroups = idx(newStreams[0], _ => _.groups);
          if (!streamRelatedGroups) {
            return;
          }

          // update the stream sections for any groups that
          // the fetched stream is related to if the fetched
          // stream is not yet in the stream section (ie.
          // brand new chat streams)
          const groupIds = streamRelatedGroups.map(group => group.id);
          groupIds.forEach(id => {
            let chatStreamsByGroupId;
            try {
              chatStreamsByGroupId = getApolloClientSingleton().readQuery({
                query: STREAM_SECTION_CHATS_QUERY,
                variables: { groupId: id },
              });
            } catch (e) {
              // do nothing
            }
            if (!chatStreamsByGroupId) {
              return;
            }

            const newStreamIsAlreadyInStreamSection = chatStreamsByGroupId.chatStreams.find(
              stream => stream.uuid === chatUUID
            );

            if (newStreamIsAlreadyInStreamSection) {
              return;
            }

            getApolloClientSingleton().writeQuery({
              query: STREAM_SECTION_CHATS_QUERY,
              variables: { groupId: id },
              data: update(chatStreamsByGroupId, {
                chatStreams: {
                  $unshift: newStreams,
                },
              }),
            });
          });
        },
        e => {
          console.warn({ e, chatUUID });
        }
      )
      .catch(e => {
        console.warn({ e, chatUUID });
        console.error('ApolloPushHandler - runtime error fetching GET_CHAT_STREAM_ITEMS_QUERY');
      });
  }

  __handleChatMessageAndBroadcastReaction(push: *) {
    push.changes.forEach(change => {
      let currentVersion;

      try {
        currentVersion = currentReactionSummaryVersion(change.content.message_uuid);
      } catch (e) {
        currentVersion = -1;
      }

      if (change.content.version <= currentVersion) {
        return;
      }

      const reactionSummaryGQL: ReactionSummaryGQL = {
        __typename: 'ReactionSummary',
        version: change.content.version,
        messageUuid: change.content.message_uuid,
        reactions: change.content.reactions.map(reaction => {
          return {
            __typename: 'Reaction',
            name: reaction.name,
            count: reaction.count,
            users: reaction.users.map(user => {
              return {
                __typename: 'ReactionUserSummary',
                uuid: user.uuid,
                name: user.name,
                initials: user.initials,
                color: user.color,
              };
            }),
            hasReacted: reaction.has_reacted,
          };
        }),
      };

      saveReactionSummary(reactionSummaryGQL);
    });
  }

  __handleCreateChatMessageChangeset(push: *) {
    const message = push.changes[0].content;
    const messageType = message.type;
    const chatUUID = message.chat.uuid;
    const threadUUID = message.thread ? message.thread.uuid : null;

    if (messageType === 'broadcast') {
      // if the message is a broadcast, check if the chat with the entity exists in the Apollo store
      this.insertIntoEntityStream(chatUUID, message);
      // check if the announcement stream for the entity exists in the Apollo store
      if (threadUUID) {
        this.insertIntoEntityStream(threadUUID, message);
      }
    }

    // then, update relevant chatStreams regardless of messageType
    this.insertIntoChatStream(chatUUID, message);
  }

  __handleCreateStreamMessageChangeset(push: *) {
    const message = push.changes[0].content;
    const streamUUID = message.stream_uuid;
    this.insertIntoEntityStream(streamUUID, message);
    this.insertIntoChatStream(streamUUID, message);
  }

  handle(push: *): void {
    try {
      switch (push.type) {
        case 'changeset-create-chat_message':
          this.__handleCreateChatMessageChangeset(push);
          break;
        case 'changeset-create-stream_message':
          this.__handleCreateStreamMessageChangeset(push);
          break;
        case 'changeset-update-chat_message_reaction':
        case 'changeset-update-chat_broadcast_reaction':
          this.__handleChatMessageAndBroadcastReaction(push);
          break;
        default:
          break;
      }
    } catch (e) {
      console.error('apollo - error while responding to push event ', push, e);
    }
  }
}

export default Handler;
